import moment, { Moment } from 'moment';
import {
    compact as _compact,
    get as _get,
    groupBy as _groupBy,
    map as _map,
    sumBy as _sumBy,
    toString as _toString,
    trim as _trim,
    uniq as _uniq,
} from 'lodash';
import { StoreConfig, StoreId } from '../../config/store-config';

// One return request from any of the three platforms, reduced to what the
// return tracker needs: who sent it back, against which order, under which
// tracking code, and how far the package has got. The platform payloads are
// wildly different (Shopee: one flat object per return; Lazada: one reverse
// order with a line per unit; TikTok: one return with line items), so each
// has its own normalizer below, and the raw payload rides along for the
// detail panel so nothing the platform said is lost.
//
// Field mappings were checked against live responses on 2026-09-22 —
// Lazada (both stores), TikTok, and Shopee (shopee-general4, list + detail).

export type ReturnPlatform = 'lazada' | 'shopee' | 'tiktok';

// Where the package physically is, as far as the platform reports it.
//
//   none        nothing ships back (refund-only request, or a cancellation)
//   pending     a package is expected but the carrier has not moved it yet
//   in_transit  the carrier has it
//   delivered   the carrier says it reached the seller
//   closed      the platform closed the case (refund paid) without saying
//               whether the package was delivered — TikTok never reports
//               delivery, and Shopee omits it until the detail is fetched
//   lost        the carrier declared it lost
//   cancelled   the return was cancelled or rejected
//   unknown     a status this code does not recognise; the raw value shows
export type PackageStage =
    | 'none'
    | 'pending'
    | 'in_transit'
    | 'delivered'
    | 'closed'
    | 'lost'
    | 'cancelled'
    | 'unknown';

// As the return-packages page shows them; the page is in Thai.
export const PACKAGE_STAGE_LABELS: Record<PackageStage, string> = {
    none: 'ไม่มีพัสดุ',
    pending: 'ยังไม่ส่ง',
    in_transit: 'กำลังส่ง',
    delivered: 'ถึงร้านแล้ว',
    closed: 'แพลตฟอร์มปิดเรื่อง',
    lost: 'สูญหาย',
    cancelled: 'ยกเลิก',
    unknown: 'ไม่ทราบ',
};

// Which stages mean a package is (or was) on its way back — the only rows
// the tracker shows. Refund-only requests and cancellations are dropped.
export const PACKAGE_EXPECTED_STAGES: PackageStage[] = [
    'pending',
    'in_transit',
    'delivered',
    'closed',
    'lost',
    'unknown',
];

// Why the package is coming back:
//
//   return           the buyer asked to return it (a platform return request)
//   failed_delivery  it never reached the buyer — refused, unreachable, or
//                    intercepted — and the courier brings it back on the
//                    ORIGINAL label (the "พัสดุตีกลับ" a shop receives)
export type PackageSource = 'return' | 'failed_delivery';

export const PACKAGE_SOURCE_LABELS: Record<PackageSource, string> = {
    return: 'คืนสินค้า',
    failed_delivery: 'ตีกลับ',
};

// One line on what each kind is, for the detail popup.
export const PACKAGE_SOURCE_DESCRIPTIONS: Record<PackageSource, string> = {
    return: 'ผู้ซื้อยื่นคำขอคืนผ่านแพลตฟอร์ม แล้วส่งพัสดุกลับมาด้วยเลขพัสดุคืน',
    failed_delivery:
        'พัสดุส่งไม่ถึงผู้ซื้อ (ติดต่อไม่ได้ / ปฏิเสธรับ / ขอยกเลิกระหว่างทาง) ขนส่งนำกลับมาคืนร้านด้วยเลขพัสดุเดิม — ไม่มีพัสดุคืนแยกต่างหาก',
};

// The platforms' request types (ReturnPackage.returnType) in Thai. Only
// values the normalizers produce or the platforms document; anything else
// shows raw.
export const RETURN_TYPE_LABELS: Record<string, string> = {
    RETURN_AND_REFUND: 'คืนสินค้าและคืนเงิน',
    RETURN: 'คืนสินค้า',
    REFUND: 'คืนเงินอย่างเดียว',
    REPLACEMENT: 'เปลี่ยนสินค้า',
    CANCEL: 'ยกเลิกก่อนจัดส่ง',
    IN_TRANSIT_REFUND: 'ขอคืนเงินระหว่างขนส่ง — ขนส่งดักพัสดุกลับ',
    FAILED_DELIVERY: 'จัดส่งไม่สำเร็จ',
};

// One step of a courier's history, where the platform gives one — Shopee's
// tracking for a parcel coming back on its forward label.
export interface TrackingEvent {
    at: Moment | null;
    status: string;
    description: string;
}

export interface ReturnItem {
    sku: string;
    name: string;
    qty: number;
}

export interface ReturnPackage {
    // `${storeId}:${returnId}` for a return, `${storeId}:fd-${orderNo}` for a
    // failed delivery — the grid key and the receipt-mark key.
    id: string;
    storeId: StoreId;
    storeTitle: string;
    platform: ReturnPlatform;
    source: PackageSource;
    // The platform's own id for the return request; '' for a failed
    // delivery, which the platforms never file as one.
    returnId: string;
    // The original order it is against, as the orders pages print it.
    orderNo: string;
    // '' until the platform has one. Lazada can carry one per line; they are
    // joined with ', ' and every one of them is searchable. A failed
    // delivery comes back on its forward label, so that is the one here.
    trackingNumber: string;
    // The courier bringing the package back. TikTok and Shopee's detail name
    // it; Lazada never does, so there — and wherever a platform leaves it
    // blank — it is read off the tracking number's prefix (carrierInferred).
    carrier: string;
    carrierInferred: boolean;
    // The platform's own wording for what was requested.
    returnType: string;
    packageStage: PackageStage;
    // Raw platform statuses, for the operator who knows the platform's
    // vocabulary better than this mapping does.
    platformStatus: string;
    logisticsStatus: string;
    // When the return was opened, and last changed.
    createdAt: Moment | null;
    updatedAt: Moment | null;
    reason: string;
    items: ReturnItem[];
    refundAmount: number | null;
    // The courier's history, newest first; [] where the platform gives none.
    trackingEvents: TrackingEvent[];
    raw: unknown;
}

// ---------------------------------------------------------------------------
// Requests waiting on the shop
//
// The platform statuses under which the next move is the seller's — approve
// or reject the request — as opposed to waiting on the buyer, the courier
// or the platform. Shopee's REQUESTED and TikTok's *_REQUEST_PENDING are
// documented as exactly that; Lazada's REQUEST_INITIATE is the line status
// before Lazada or the seller has acted (observed statuses after it:
// REFUND_SUCCESS, REFUND_REJECT, REQUEST_CANCEL).

const SELLER_DECISION_STATUSES: Record<ReturnPlatform, string[]> = {
    shopee: ['REQUESTED'],
    tiktok: ['RETURN_OR_REFUND_REQUEST_PENDING', 'REPLACEMENT_REQUEST_PENDING'],
    lazada: ['REQUEST_INITIATE'],
};

export const awaitingSellerDecision = (row: ReturnPackage): boolean => {
    // Lazada's status is the distinct line statuses joined with ' / '.
    const statuses = row.platformStatus.split(' / ').map((s) => s.trim());
    return statuses.some((status) =>
        SELLER_DECISION_STATUSES[row.platform].includes(status),
    );
};

// ---------------------------------------------------------------------------
// Searching
//
// What an operator types or scans is an order number, a tracking code or a
// return id, and the label it came off may space or hyphenate it. Codes are
// compared with everything but letters and digits removed, case-folded, so
// "THT-2125751577" and "tht 2125751577" both find "THT2125751577".

export const normalizeCode = (value: unknown): string =>
    _toString(value)
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, '');

// Shortest typed fragment that may match by containment rather than
// equality. Shorter than this and a partial order number would light up
// half the table.
const MIN_PARTIAL_CODE_LENGTH = 6;

export const returnSearchKeys = (row: ReturnPackage): string[] =>
    _uniq(
        _compact(
            [row.returnId, row.orderNo, ...row.trackingNumber.split(',')].map(
                normalizeCode,
            ),
        ),
    );

// Whether a typed or scanned code identifies any of `keys` (already
// normalized): equal, or — once the fragment is long enough to be specific —
// contained either way, since a scanner may add a prefix or suffix to the
// code printed on the label.
export const codeMatchesAny = (keys: string[], code: unknown): boolean => {
    const needle = normalizeCode(code);
    if (!needle) {
        return false;
    }
    return keys.some(
        (key) =>
            key.length > 0 &&
            (key === needle ||
                (needle.length >= MIN_PARTIAL_CODE_LENGTH &&
                    (key.includes(needle) ||
                        (key.length >= MIN_PARTIAL_CODE_LENGTH &&
                            needle.includes(key))))),
    );
};

export const matchesCode = (row: ReturnPackage, code: unknown): boolean =>
    codeMatchesAny(returnSearchKeys(row), code);

// ---------------------------------------------------------------------------
// Courier from the tracking number
//
// Only Lazada omits the courier outright, but a platform can also leave the
// field blank, so this is the fallback for all three. Prefixes as seen on
// the shops' own return tracking numbers (2026-09-22): Lazada KERCRD… /
// LEXCRPU… / TH01…, Shopee TH010… / SDOFRTN…, TikTok THT… / JTTH…. Unknown
// prefixes give '' rather than a guess.

const COURIER_BY_PREFIX: [RegExp, string][] = [
    [/^LEX/, 'LEX (Lazada Express)'],
    [/^KER/, 'Kerry Express'],
    [/^JTTH/, 'J&T Express'],
    [/^SPXTH/, 'SPX Express'],
    [/^SDOFRTN/, 'KEX Express'],
    [/^(NVTH|NINJA)/, 'Ninja Van'],
    [/^THT\d/, 'Flash Express'],
    [/^TH\d{2}/, 'Flash Express'],
    [/^[A-Z]{2}\d{9}TH$/, 'Thailand Post'],
];

export const inferCourier = (trackingNumber: string): string => {
    const code = normalizeCode(trackingNumber.split(',')[0]);
    const match = COURIER_BY_PREFIX.find(([pattern]) => pattern.test(code));
    return match ? match[1] : '';
};

// The courier fields as a normalizer fills them: the platform's name when
// it gives one, else the inference.
const courierFields = (
    platformName: unknown,
    trackingNumber: string,
): { carrier: string; carrierInferred: boolean } => {
    const named = _trim(_toString(platformName ?? ''));
    if (named) {
        return { carrier: named, carrierInferred: false };
    }
    const inferred = inferCourier(trackingNumber);
    return { carrier: inferred, carrierInferred: inferred !== '' };
};

// ---------------------------------------------------------------------------
// Shared helpers

const unixToMoment = (value: unknown): Moment | null => {
    const seconds = Number(value);
    return Number.isFinite(seconds) && seconds > 0
        ? moment.unix(seconds)
        : null;
};

const toNumberOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

// One line per SKU. Units arrive one entry each on Lazada and TikTok (so
// each counts 1); Shopee carries a quantity per entry (`qtyOf`). Folding
// every platform the same way keeps the SKU unique within a return, which
// the inspection page relies on for its row key.
const groupUnits = (
    units: any[],
    skuOf: (unit: any) => unknown,
    nameOf: (unit: any) => unknown,
    qtyOf: (unit: any) => number = () => 1,
): ReturnItem[] =>
    _map(
        _groupBy(units, (unit) => _toString(skuOf(unit))),
        (group: any[], sku: string): ReturnItem => ({
            sku,
            name: _toString(nameOf(group[0]) ?? ''),
            qty: _sumBy(group, (unit) => Math.max(0, qtyOf(unit)) || 0),
        }),
    );

const packageId = (store: StoreConfig, returnId: string): string =>
    `${store.storeId}:${returnId}`;

// ---------------------------------------------------------------------------
// Shopee — v2 returns/get_return_list, optionally merged with
// returns/get_return_detail for the same return_sn.
//
// The list has no `logistics_status`; only the detail does, and it is the
// one field that says the carrier delivered the package back. Rows built
// from the list alone therefore never reach 'delivered' — they sit at
// 'pending' or 'closed' — which is why the fetch service asks for the detail
// of every return it shows.

const SHOPEE_LOGISTICS_STAGE: Record<string, PackageStage> = {
    LOGISTICS_DELIVERY_DONE: 'delivered',
    LOGISTICS_PICKUP_DONE: 'in_transit',
    LOGISTICS_PICKUP_RETRY: 'in_transit',
    LOGISTICS_DELIVERY_FAILED: 'in_transit',
    LOGISTICS_LOST: 'lost',
    LOGISTICS_REQUEST_CANCELED: 'cancelled',
    LOGISTICS_COD_REJECTED: 'cancelled',
    // Nothing has moved yet: fall through to the request status.
    LOGISTICS_READY: 'pending',
    LOGISTICS_REQUEST_CREATED: 'pending',
    LOGISTICS_PICKUP_FAILED: 'pending',
    LOGISTICS_NOT_STARTED: 'pending',
    LOGISTICS_PENDING_ARRANGE: 'pending',
};

const shopeeStage = (
    status: string,
    logisticsStatus: string,
    needsLogistics: unknown,
    returnSolution: unknown,
): PackageStage => {
    // return_solution 1 is "refund only"; needs_logistics false says the same
    // from the logistics side. Either way nothing ships back.
    if (needsLogistics === false || Number(returnSolution) === 1) {
        return 'none';
    }
    if (logisticsStatus) {
        const mapped = SHOPEE_LOGISTICS_STAGE[logisticsStatus];
        if (mapped === undefined) {
            return 'unknown';
        }
        if (mapped !== 'pending') {
            return mapped;
        }
    }
    switch (status) {
        case 'CANCELLED':
            return 'cancelled';
        case 'REFUND_PAID':
        case 'CLOSED':
            return 'closed';
        // ACCEPTED is where Shopee leaves a refunded return (no return in
        // June–September 2026 ever read REFUND_PAID). Reaching here, the
        // return leg never moved (READY / NOT_STARTED …): the buyer was
        // refunded without sending the goods back. With no logistics status
        // at all (no detail), where the parcel is cannot be told — closed.
        case 'ACCEPTED':
            return logisticsStatus ? 'none' : 'closed';
        default:
            return 'pending';
    }
};

// A refund the buyer raised while the parcel was still on its way to them
// (return_refund_request_type 1). The courier intercepts it and brings it
// back on the ORIGINAL label — so, unlike a refund-only case, a parcel does
// come back, even though the return row says needs_logistics false and has
// no return tracking number. Verified on three shopee-general4 orders
// (2026-06..08). lnwshop does NOT reverse these sales (it does for a failed
// delivery), so they are the ones whose stock has to be put back by hand.
export const isShopeeInTransitRefund = (row: any): boolean =>
    Number(_get(row, 'return_refund_request_type')) === 1;

// The forward label of an order and its logistics/get_tracking_info history
// — where a parcel coming back on that label reports its progress.
export interface ShopeeForwardTracking {
    trackingNumber: string;
    // The order's shipping_carrier. SPX and Flash labels both start with TH,
    // so the prefix alone cannot tell them apart.
    carrier?: string;
    events: any[];
}

// How far back along the forward label a parcel has got. RETURNED is the
// courier handing it to the shop (RETURN_INITIATED / RETURN_STARTED /
// RETURN_PENDING come before); anything short of it is still on the road.
export const shopeeReturnLeg = (
    events: any[],
): {
    stage: PackageStage;
    latest: string;
    arrivedAt: Moment | null;
    events: TrackingEvent[];
} => {
    const sorted = [...(events || [])].sort(
        (a, b) => Number(b?.update_time) - Number(a?.update_time),
    );
    const arrival = sorted.find(
        (event) => event?.logistics_status === 'RETURNED',
    );
    return {
        stage: arrival ? 'delivered' : 'in_transit',
        latest: _toString(sorted[0]?.logistics_status ?? ''),
        arrivedAt: arrival ? unixToMoment(arrival.update_time) : null,
        events: sorted.map((event) => ({
            at: unixToMoment(event?.update_time),
            status: _toString(event?.logistics_status ?? ''),
            description: _trim(_toString(event?.description ?? '')),
        })),
    };
};

// `forward` is only used for an in-transit refund (see above), whose parcel
// is tracked on the order's own label.
export const normalizeShopeeReturn = (
    store: StoreConfig,
    listRow: any,
    detail?: any,
    forward?: ShopeeForwardTracking,
): ReturnPackage => {
    // The detail repeats every list field and adds the logistics ones, so it
    // wins wherever both carry a value.
    const row = { ...(listRow || {}), ...(detail || {}) };
    const returnId = _toString(row.return_sn);
    const status = _toString(row.status ?? '');
    const intercepted = isShopeeInTransitRefund(row);
    const leg = forward ? shopeeReturnLeg(forward.events) : null;
    const logisticsStatus = intercepted
        ? (leg?.latest ?? '')
        : _toString(row.logistics_status ?? '');
    const trackingNumber = _trim(
        _toString(
            (intercepted ? forward?.trackingNumber : row.tracking_number) ?? '',
        ),
    );
    return {
        id: packageId(store, returnId),
        storeId: store.storeId,
        storeTitle: store.title,
        platform: 'shopee',
        source: intercepted ? 'failed_delivery' : 'return',
        returnId,
        orderNo: _toString(row.order_sn ?? ''),
        trackingNumber,
        ...courierFields(
            intercepted ? forward?.carrier : row.reverse_logistics_channel_name,
            trackingNumber,
        ),
        returnType: intercepted
            ? 'IN_TRANSIT_REFUND'
            : Number(row.return_solution) === 1
              ? 'REFUND'
              : Number(row.return_solution) === 0
                ? 'RETURN_AND_REFUND'
                : _toString(row.return_solution ?? ''),
        packageStage: intercepted
            ? status === 'CANCELLED'
                ? 'cancelled'
                : (leg?.stage ?? 'in_transit')
            : shopeeStage(
                  status,
                  logisticsStatus,
                  row.needs_logistics,
                  row.return_solution,
              ),
        platformStatus: status,
        logisticsStatus,
        createdAt: unixToMoment(row.create_time),
        updatedAt: leg?.arrivedAt ?? unixToMoment(row.update_time),
        reason: _compact([
            _toString(row.reason ?? ''),
            _toString(row.text_reason ?? ''),
        ]).join(' — '),
        items: groupUnits(
            row.item || [],
            (item) => item.variation_sku || item.item_sku || '',
            (item) => item.name,
            (item) => Number(item.amount) || 1,
        ),
        refundAmount: toNumberOrNull(row.refund_amount),
        trackingEvents: leg?.events ?? [],
        raw: forward ? { ...row, forward_tracking: forward } : row,
    };
};

// ---------------------------------------------------------------------------
// Lazada — /reverse/getreverseordersforseller. One reverse order per row with
// a `reverse_order_lines` entry per unit; the line carries the statuses, the
// tracking number and the amounts, the header only the ids and the type.

// `ofc_status` is the carrier's view. Only the values observed on 2026-09-22
// are mapped; other INFO_ST_* codes are carrier progress and read as in
// transit, anything else is 'unknown' and shows raw.
const LAZADA_OFC_STAGE: Record<string, PackageStage> = {
    RETURN_DELIVERED: 'delivered',
    RETURN_DELIVERED_LOST: 'lost',
    RETURN_CANCELED: 'cancelled',
    INFO_ST_DRIVER_ASSIGNED: 'in_transit',
};

// Most informative first: a reverse order whose lines disagree reports the
// line that says the most about where the package is.
const STAGE_PRIORITY: PackageStage[] = [
    'delivered',
    'lost',
    'in_transit',
    'pending',
    'unknown',
    'closed',
    'cancelled',
    'none',
];

const mostInformativeStage = (stages: PackageStage[]): PackageStage =>
    STAGE_PRIORITY.find((stage) => stages.includes(stage)) ?? 'none';

const lazadaLineStage = (line: any): PackageStage => {
    const ofcStatus = _toString(line.ofc_status ?? '');
    if (ofcStatus) {
        return (
            LAZADA_OFC_STAGE[ofcStatus] ??
            (ofcStatus.startsWith('INFO_ST_') ? 'in_transit' : 'unknown')
        );
    }
    if (_trim(_toString(line.tracking_number ?? ''))) {
        return 'pending';
    }
    switch (_toString(line.reverse_status ?? '')) {
        // Refunded with no carrier record at all: the platform settled it
        // without asking for the goods back.
        case 'REFUND_SUCCESS':
            return 'none';
        case 'REFUND_REJECT':
        case 'REQUEST_CANCEL':
            return 'cancelled';
        default:
            return 'pending';
    }
};

// Lazada's reverse amounts are in satang (item_unit_price 6500 for a ฿65
// SKU), unlike its order endpoints, which quote baht.
const LAZADA_AMOUNT_DIVISOR = 100;

export const normalizeLazadaReverseOrder = (
    store: StoreConfig,
    row: any,
): ReturnPackage => {
    const lines: any[] = row.reverse_order_lines || [];
    const returnId = _toString(row.reverse_order_id);
    const requestType = _toString(row.request_type ?? '');
    const created = _compact(
        lines.map((line) => unixToMoment(line.return_order_line_gmt_create)),
    );
    const modified = _compact(
        lines.map((line) => unixToMoment(line.return_order_line_gmt_modified)),
    );
    const trackingNumbers = _uniq(
        _compact(
            lines.map((line) => _trim(_toString(line.tracking_number ?? ''))),
        ),
    );
    return {
        id: packageId(store, returnId),
        storeId: store.storeId,
        storeTitle: store.title,
        platform: 'lazada',
        source: 'return',
        returnId,
        orderNo: _toString(row.trade_order_id ?? ''),
        trackingNumber: trackingNumbers.join(', '),
        ...courierFields('', trackingNumbers.join(', ')),
        returnType: requestType,
        // A CANCEL request is a pre-shipment cancellation, never a package.
        packageStage:
            requestType === 'CANCEL'
                ? 'none'
                : mostInformativeStage(lines.map(lazadaLineStage)),
        platformStatus: _uniq(
            _compact(lines.map((line) => _toString(line.reverse_status ?? ''))),
        ).join(' / '),
        logisticsStatus: _uniq(
            _compact(lines.map((line) => _toString(line.ofc_status ?? ''))),
        ).join(' / '),
        createdAt: created.length ? moment.min(created) : null,
        updatedAt: modified.length ? moment.max(modified) : null,
        reason: _toString(_get(lines, '[0].reason_text') ?? ''),
        // Lazada names only ids here; the seller SKU is the readable one.
        items: groupUnits(
            lines,
            (line) => line.seller_sku_id || line.platform_sku_id || '',
            () => '',
        ),
        refundAmount: lines.length
            ? _sumBy(lines, (line) =>
                  line.is_need_refund === false
                      ? 0
                      : (toNumberOrNull(line.refund_amount) ?? 0),
              ) / LAZADA_AMOUNT_DIVISOR
            : null,
        trackingEvents: [],
        raw: row,
    };
};

// ---------------------------------------------------------------------------
// TikTok — /return_refund/202309/returns/search. The status enum tracks the
// request, not the parcel: BUYER_SHIPPED_ITEM is the last thing said about
// the package before the case closes, so a delivered-but-not-yet-refunded
// return still reads as in transit here.

const TIKTOK_STATUS_STAGE: Record<string, PackageStage> = {
    RETURN_OR_REFUND_REQUEST_PENDING: 'pending',
    AWAITING_BUYER_SHIP: 'pending',
    AWAITING_BUYER_RESPONSE: 'pending',
    REPLACEMENT_REQUEST_PENDING: 'pending',
    BUYER_SHIPPED_ITEM: 'in_transit',
    RETURN_OR_REFUND_REQUEST_SUCCESS: 'closed',
    RETURN_OR_REFUND_REQUEST_COMPLETE: 'closed',
    REPLACEMENT_REQUEST_COMPLETE: 'closed',
    REPLACEMENT_REQUEST_REFUND_SUCCESS: 'closed',
    RETURN_OR_REFUND_REQUEST_CANCEL: 'cancelled',
    REFUND_OR_RETURN_REQUEST_REJECT: 'cancelled',
    REPLACEMENT_REQUEST_REJECT: 'cancelled',
    REPLACEMENT_REQUEST_CANCEL: 'cancelled',
};

// A closed case with no return tracking number never shipped anything back:
// on low-value items (under ~฿100) TikTok refunds the buyer and waives the
// return, because the postage would cost more than the goods. Such a
// RETURN_AND_REFUND is a refund-only in practice, so there is no package
// for the tracker. An OPEN case without tracking is different — the buyer
// simply has not shipped yet — and stays 'pending'.
const tiktokStage = (
    returnType: string,
    status: string,
    trackingNumber: string,
): PackageStage => {
    if (returnType === 'REFUND') {
        return 'none';
    }
    const stage = TIKTOK_STATUS_STAGE[status] ?? 'unknown';
    return stage === 'closed' && !trackingNumber ? 'none' : stage;
};

export const normalizeTiktokReturn = (
    store: StoreConfig,
    row: any,
): ReturnPackage => {
    const returnId = _toString(row.return_id);
    const returnType = _toString(row.return_type ?? '');
    const status = _toString(row.return_status ?? '');
    const trackingNumber = _trim(_toString(row.return_tracking_number ?? ''));
    return {
        id: packageId(store, returnId),
        storeId: store.storeId,
        storeTitle: store.title,
        platform: 'tiktok',
        source: 'return',
        returnId,
        orderNo: _toString(row.order_id ?? ''),
        trackingNumber,
        ...courierFields(row.return_provider_name, trackingNumber),
        returnType,
        packageStage: tiktokStage(returnType, status, trackingNumber),
        platformStatus: status,
        logisticsStatus: '',
        createdAt: unixToMoment(row.create_time),
        updatedAt: unixToMoment(row.update_time),
        reason: _toString(row.return_reason_text || row.return_reason || ''),
        items: groupUnits(
            row.return_line_items || [],
            (item) => item.seller_sku || item.sku_id || '',
            (item) => item.product_name || item.sku_name || '',
        ),
        refundAmount: toNumberOrNull(_get(row, 'refund_amount.refund_total')),
        trackingEvents: [],
        raw: row,
    };
};

// ---------------------------------------------------------------------------
// Failed deliveries ("พัสดุตีกลับ") — parcels that never reached the buyer and
// come back on their ORIGINAL label. None of the platforms files these as a
// return request, so they come from the ORDER endpoints. Signals verified
// against live data on 2026-09-23 (Lazada 160 orders since 2024, Shopee 19
// and TikTok 17 in 90 days):
//
//   Lazada  items move shipped -> shipped_back (travelling back; LEX last
//           mile only — Flash/Kerry skip it) -> shipped_back_success (back
//           at the shop). Listed by /orders/get status=failed | shipped_back.
//   Shopee  the order turns CANCELLED ('Failed Delivery', by system) when
//           the courier starts the last leg back; it had been handed over
//           (pickup_done_time) and its package reads
//           LOGISTICS_DELIVERY_FAILED. Arrival is only in the tracking
//           history (RETURNED).
//   TikTok  the order turns CANCELLED at the moment the parcel is back at
//           the shop; collection_time (the courier had it) is what sets it
//           apart from a cancel before handover. Do not use the
//           cancellation initiator or reason text: both flip between calls.

const fdId = (store: StoreConfig, orderNo: string): string =>
    packageId(store, `fd-${orderNo}`);

// A cancelled Shopee order whose parcel had been handed to the courier.
// Cancels before handover (249 of 249 checked) have no pickup_done_time and
// a package that never reached LOGISTICS_DELIVERY_FAILED.
export const isShopeeFailedDelivery = (order: any): boolean =>
    Number(_get(order, 'pickup_done_time')) > 0 ||
    (_get(order, 'package_list') || []).some(
        (pkg: any) => pkg?.logistics_status === 'LOGISTICS_DELIVERY_FAILED',
    );

// A cancelled TikTok order the courier had collected.
export const isTiktokFailedDelivery = (order: any): boolean =>
    Number(_get(order, 'collection_time')) > 0;

// Lazada order/item times read '2026-09-21 15:33:32 +0700'.
const lazadaTime = (value: unknown): Moment | null => {
    const parsed = moment(
        _toString(value ?? ''),
        'YYYY-MM-DD HH:mm:ss ZZ',
        true,
    );
    return parsed.isValid() ? parsed : null;
};

// 'Pickup: LEX TH, Delivery: Kerry' — the last-mile carrier is the one whose
// label is on the parcel.
const lazadaDeliveryCarrier = (provider: unknown): string =>
    _trim(/Delivery:\s*([^,]+)/.exec(_toString(provider ?? ''))?.[1] ?? '');

// One Lazada order with its /orders/items/get rows (one per unit), or null
// when none of its items is on the way back. Items are judged one by one: a
// mixed order can hold a delivered or cancelled item next to a returned one.
export const normalizeLazadaFailedDelivery = (
    store: StoreConfig,
    order: any,
    items: any[],
): ReturnPackage | null => {
    const units = (items || []).filter((item) =>
        _toString(item?.status).startsWith('shipped_back'),
    );
    if (units.length === 0) {
        return null;
    }
    const orderNo = _toString(order?.order_id ?? units[0].order_id ?? '');
    const statuses = _uniq(units.map((unit) => _toString(unit.status)));
    const trackingNumbers = _uniq(
        _compact(
            units.map((unit) => _trim(_toString(unit.tracking_code ?? ''))),
        ),
    );
    const times = _compact(units.map((unit) => lazadaTime(unit.updated_at)));
    const trackingNumber = trackingNumbers.join(', ');
    return {
        id: fdId(store, orderNo),
        storeId: store.storeId,
        storeTitle: store.title,
        platform: 'lazada',
        source: 'failed_delivery',
        returnId: '',
        orderNo,
        trackingNumber,
        ...courierFields(
            lazadaDeliveryCarrier(units[0].shipment_provider),
            trackingNumber,
        ),
        returnType: 'FAILED_DELIVERY',
        packageStage: statuses.includes('shipped_back_success')
            ? 'delivered'
            : statuses.includes('shipped_back')
              ? 'in_transit'
              : 'unknown',
        platformStatus: statuses.join(' / '),
        logisticsStatus: '',
        // When it became a returning parcel (an item's updated_at moves with
        // each status change; at shipped_back_success it is the arrival).
        createdAt: times.length
            ? moment.min(times)
            : lazadaTime(order?.updated_at),
        updatedAt: times.length
            ? moment.max(times)
            : lazadaTime(order?.updated_at),
        reason: _toString(units[0].reason ?? ''),
        items: groupUnits(
            units,
            (unit) => unit.sku || unit.shop_sku || '',
            (unit) => unit.name,
        ),
        // Nothing to refund: a failed COD delivery was never paid.
        refundAmount: null,
        trackingEvents: [],
        raw: { order, items: units },
    };
};

// A cancelled Shopee order that failed delivery (see isShopeeFailedDelivery),
// with its forward label and tracking history.
export const normalizeShopeeFailedDelivery = (
    store: StoreConfig,
    order: any,
    forward: ShopeeForwardTracking,
): ReturnPackage => {
    const orderNo = _toString(order?.order_sn ?? '');
    const leg = shopeeReturnLeg(forward.events);
    const trackingNumber = _trim(_toString(forward.trackingNumber ?? ''));
    return {
        id: fdId(store, orderNo),
        storeId: store.storeId,
        storeTitle: store.title,
        platform: 'shopee',
        source: 'failed_delivery',
        returnId: '',
        orderNo,
        trackingNumber,
        ...courierFields(
            forward.carrier || order?.shipping_carrier,
            trackingNumber,
        ),
        returnType: 'FAILED_DELIVERY',
        packageStage: leg.stage,
        platformStatus: _toString(order?.order_status ?? ''),
        logisticsStatus: leg.latest,
        // update_time is when Shopee cancelled it: the start of the last leg.
        createdAt: unixToMoment(order?.update_time),
        updatedAt: leg.arrivedAt ?? unixToMoment(order?.update_time),
        reason: _toString(order?.cancel_reason ?? ''),
        items: groupUnits(
            order?.item_list || [],
            (item) => item.model_sku || item.item_sku || '',
            (item) => item.item_name,
            (item) => Number(item.model_quantity_purchased) || 1,
        ),
        refundAmount: null,
        trackingEvents: leg.events,
        raw: { order, tracking: forward },
    };
};

// A cancelled TikTok order that failed delivery (see isTiktokFailedDelivery).
// TikTok cancels it at the moment the courier returns it to the shop, so it
// is always back by the time it shows up here.
export const normalizeTiktokFailedDelivery = (
    store: StoreConfig,
    order: any,
): ReturnPackage => {
    const orderNo = _toString(order?.id ?? '');
    const lineItems: any[] = order?.line_items || [];
    const trackingNumber = _trim(_toString(order?.tracking_number ?? ''));
    return {
        id: fdId(store, orderNo),
        storeId: store.storeId,
        storeTitle: store.title,
        platform: 'tiktok',
        source: 'failed_delivery',
        returnId: '',
        orderNo,
        trackingNumber,
        ...courierFields(
            lineItems[0]?.shipping_provider_name || order?.shipping_provider,
            trackingNumber,
        ),
        returnType: 'FAILED_DELIVERY',
        packageStage: 'delivered',
        platformStatus: _toString(order?.status ?? ''),
        logisticsStatus: '',
        createdAt: unixToMoment(order?.cancel_time),
        updatedAt: unixToMoment(order?.cancel_time),
        reason: _toString(order?.cancel_reason ?? ''),
        // One line item per unit.
        items: groupUnits(
            lineItems,
            (item) => item.seller_sku || item.sku_id || '',
            (item) => item.product_name || item.sku_name || '',
        ),
        refundAmount: null,
        trackingEvents: [],
        raw: order,
    };
};
